import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { RefreshCw, Copy, CheckCircle2, AlertTriangle } from 'lucide-react';

const card = { background: 'var(--field)', border: '1px solid var(--rule-strong)', borderRadius: 16, padding: '16px 18px', marginBottom: 12 };
const lbl = { fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--ink-mute)', marginBottom: 4, display: 'block' };
const code = { fontFamily: 'monospace', fontSize: 'var(--t-note)', color: 'var(--ink)', wordBreak: 'break-all' };

export default function AdminNsSheet() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setStatus(await api.getNsSheetStatus()); }
    catch (e) { setError(e.message); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const handleSync = async () => {
    setSyncing(true); setError(null); setResult(null);
    try { setResult(await api.syncNsSheet()); }
    catch (e) { setError(e.message); }
    setSyncing(false);
  };

  const copyEmail = () => {
    if (!status?.service_account_email) return;
    navigator.clipboard?.writeText(status.service_account_email);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: 'var(--s6) var(--s4) 120px' }}>
      <h1 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 800, color: 'var(--ink)'}}> NSスプレッドシート連携</h1>
      <p style={{ margin: '0 0 20px', fontSize: 'var(--t-note)', color: 'var(--ink-soft)', lineHeight: 1.6 }}>
        ユーザー管理で「NSシートに載せる」をオンにした人の日次KPIをGoogleスプレッドシートへ同期します（オン・オフは本部の管理者が切り替えます）。日報・時間別入力・着座メモ・フィードバックの内容は、<b>毎時（JST 10〜0時）の自動同期で反映</b>されます（保存してから最大1時間かかります）。誰が閲覧できるかは、そのスプレッドシートの共有設定で管理してください。
      </p>

      {loading ? (
        <div style={{ ...card, color: 'var(--ink-mute)', fontSize: 'var(--t-note)' }}>読み込み中...</div>
      ) : (
        <>
          {/* セットアップ手順 */}
          <div style={card}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)', marginBottom: 12 }}>セットアップ手順</div>
            <ol style={{ margin: 0, paddingLeft: 18, fontSize: 'var(--t-note)', color: 'var(--ink)', lineHeight: 1.9 }}>
              <li>Googleスプレッドシートを新規作成する</li>
              <li>下のサービスアカウントのメールを、そのシートに<b>「編集者」</b>で共有する</li>
              <li>シートURLの <span style={code}>/d/●●●/</span> のID（NS_SHEET_ID）を設定してもらう</li>
              <li>下の「今すぐ同期」を押す → タブ「{status?.tab || 'NS_KPI'}」に書き込まれる</li>
            </ol>
          </div>

          {/* 共有先メール */}
          <div style={card}>
            <label style={lbl}>共有先（サービスアカウントのメール）</label>
            {status?.service_account_email ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ ...code, flex: 1 }}>{status.service_account_email}</span>
                <button onClick={copyEmail} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 10px', borderRadius: 8, border: '1px solid var(--rule-strong)', background: 'transparent', color: copied ? 'var(--gain)' : 'var(--ink)', fontSize: 'var(--t-note)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                  {copied ? <><CheckCircle2 size={13} /> コピー済</> : <><Copy size={13} /> コピー</>}
                </button>
              </div>
            ) : (
              <div style={{ fontSize: 'var(--t-note)', color: 'var(--hold)' }}>サービスアカウント未設定（FCM_SERVICE_ACCOUNT）</div>
            )}
          </div>

          {/* 設定状況 */}
          <div style={card}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={lbl}>スプレッドシートID</label>
                <div style={{ fontSize: 'var(--t-note)', fontWeight: 700, color: status?.sheet_id_set ? 'var(--gain)' : 'var(--hold)' }}>
                  {status?.sheet_id_set ? '設定済み' : '未設定（NS_SHEET_ID）'}
                </div>
              </div>
              <div>
                {/* 対象＝ユーザー管理で「NSシートに載せる」をオンにした人（users.ns_sheet。ADR 0033） */}
                <label style={lbl}>シート対象者（有効）</label>
                <div style={{ fontSize: 'var(--t-note)', fontWeight: 700, color: 'var(--ink)' }}>{status?.ns_user_count ?? 0} 人</div>
              </div>
            </div>
          </div>

          {/* 同期ボタン */}
          <button
            onClick={handleSync}
            disabled={syncing || !status?.sheet_id_set}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', padding: '12px 16px', background: status?.sheet_id_set ? 'var(--grad-primary)' : 'var(--rule)', border: 'none', borderRadius: 12, color: '#fff', fontWeight: 700, fontSize: 14, cursor: status?.sheet_id_set ? 'pointer' : 'not-allowed', opacity: syncing ? 0.7 : 1 }}
          >
            <RefreshCw size={15} className={syncing ? 'spin' : ''} /> {syncing ? '同期中...' : '今すぐ同期'}
          </button>
          <p style={{ margin: '10px 2px 0', fontSize: 'var(--t-note)', color: 'var(--ink-soft)' }}>
            ※ 自動同期は毎時（JST 10〜0時）です。保存した内容をすぐシートに出したいときは、このボタンで今すぐ全件を同期してください。
          </p>

          {result && (
            <div style={{ ...card, marginTop: 12, border: '1px solid #1E5540' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--gain)', fontSize: 'var(--t-note)', fontWeight: 700 }}>
                <CheckCircle2 size={15} /> {result.message}
              </div>
              {result.synced_at && <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-mute)', marginTop: 6 }}>最終同期: {result.synced_at}（JST） / タブ: {result.tab}</div>}
            </div>
          )}
          {error && (
            <div style={{ ...card, marginTop: 12, border: '1px solid #5C4718' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--hold)', fontSize: 'var(--t-note)' }}>
                <AlertTriangle size={15} /> {error}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
